/* File: App.js
 * Path: ananunadm/src/App.js
 */
import { useState, useEffect, useRef } from "react";
import { Router, Route } from "react-router-dom";

// Your existing imports
import history from "./helpers/history";
import { cookies, setCookies } from "./helpers/cookies";
import { isLoggedIn } from "./helpers/auth";
import { UserContext } from "./helpers/userContext";
import { SocketProvider, disconnectSocket } from "./helpers/socket";
import API from "./helpers/api";
import {
	isAuthenticationFailure,
	registerSessionExpiredHandler,
	refreshPrivateTokenIfNeeded
} from "./helpers/api";
import { useStateWithLocalStorage } from "./helpers/storage";
import config from "./config";
import { Language } from "./helpers/language";
import { Global } from "./helpers/global";
import { BrowserStateProvider } from "./helpers/browserState";
import { ActivePlatform } from "./helpers/activePlatform.jsx";
import { PermissionContext } from "./helpers/permissionsContext.js";
import { GlobalCallingProvider } from "./helpers/globalCalling.jsx";
import { IsMobileProvider } from "./helpers/is-mobile.jsx";
import { setTenantConfig, tenantModuleShapeChanged } from "./helpers/tenantConfig";
// Global components
import Login from "./components/login";
import Main from "./components/main/main";
import PasswordChangeGate from "./components/auth/PasswordChangeGate";
import Loading from "./helpers/loading";
import PublicKnowledgeArticle from "./pages/Knowledge/PublicKnowledgeArticle";
import PublicCareers from "./pages/Careers/PublicCareers";
import PublicFormPage from "./pages/Forms/PublicFormPage";
import PublicAlbum from "./pages/PublicAlbum/PublicAlbum";

// Import the new environment-based router
import { EnvironmentBasedRouter } from "./EnvironmentBasedRouter";

// Mantine components
import "./global.css";
import { MantineProvider, ColorSchemeProvider } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { Notifications, notifications } from "@mantine/notifications";
import { glassTheme } from "./theme/glassTheme";
import { getDateLocale } from "./helpers/dateLocale";
import { applyThemeCssVariables } from "./theme/themeSettings";
import { setLongTimeout } from "./helpers/longTimeout";
import {
	ACTIVITY_IDLE_AFTER_MS,
	ACTIVITY_SYNC_KEY,
	readLastActivityAt,
	writeLastActivityAt
} from "./helpers/activitySession";

// Stable component reference — defined outside App so it never changes between renders
function RouterWrapper() {
	return <EnvironmentBasedRouter />;
}

const LOGOUT_SYNC_KEY = "apit:auth:logout";

function isPublicFrontendRoute(pathname = "") {
	return (
		pathname.startsWith("/public/knowledge/") ||
		pathname === "/careers" ||
		pathname.startsWith("/careers/") ||
		pathname.startsWith("/public/meet/") ||
		pathname.startsWith("/public/album/") ||
		pathname.startsWith("/f/")
	);
}

// glassTheme replaces the old inline theme object — all design tokens live in src/theme/glassTheme.js
const theme = glassTheme;

if (typeof document !== "undefined") {
	applyThemeCssVariables();
}

function App() {
	let [loggedIn, setLoggedIn] = useState(false);
	let [user, setUser] = useState(null);
	const [authChecking, setAuthChecking] = useState(() => !isPublicFrontendRoute(
		typeof window !== "undefined" ? window.location.pathname : ""
	));
	const [permissions, setPermissions] = useState([]);
	let [lang, setLang] = useStateWithLocalStorage("language");
	const [colorScheme, setColorScheme] = useStateWithLocalStorage("mantine-color-scheme-value");

	// Keep <html data-color-scheme> in sync so global.css glass variables switch correctly.
	// The public form page renders its own light MantineProvider on a light page bg, so
	// pin the document scheme to light for it — otherwise the default "dark" glass
	// variables leak through the global .mantine-Card-root !important rule and produce
	// dark cards with light-theme (dark) text, an inconsistent mixed-theme look.
	useEffect(() => {
		const onPublicForm =
			typeof window !== "undefined" && window.location.pathname.startsWith("/f/");
		document.documentElement.dataset.colorScheme = onPublicForm ? "light" : colorScheme || "dark";
	}, [colorScheme]);

	// Native date/month inputs use the document language, while Mantine calendars
	// use DatesProvider below. Keep both picker types aligned with the platform language.
	useEffect(() => {
		document.documentElement.lang = getDateLocale(lang);
	}, [lang]);

	const [activePlatform, setActivePlatform] = useStateWithLocalStorage("activePlatform");
	const [activePage, setActivePage] = useStateWithLocalStorage("activePage");
	const [subActivePage, setSubActivePage] = useStateWithLocalStorage("subActivePage");
	const [platforms, setPlatforms] = useState([]);
	const [filteredMenu, setFilteredMenu] = useState([]);
	const [currentPlatformIndex, setCurrentPlatformIndex] = useState(-1);
	const logoutInProgressRef = useRef(false);
	const currentPathname =
		typeof window !== "undefined" ? window.location.pathname : history.location?.pathname || "";
	const isPublicPage = isPublicFrontendRoute(currentPathname);

	function applyAuthenticatedSession(authPayload, tokenOverride = null) {
		const nextToken = tokenOverride || authPayload?.token;
		if (nextToken) {
			setCookies(nextToken);
		}
		if (authPayload?.tenant) {
			// config.js computes its platform menu once from this cache at script-load
			// time, so a config that only became known just now (first login on this
			// browser, or a tenant switch) leaves that menu permanently stale until the
			// page re-evaluates it. Reload once so it picks up the fresh value.
			const needsReload = tenantModuleShapeChanged(authPayload.tenant);
			setTenantConfig(authPayload.tenant);
			if (needsReload) {
				window.location.reload();
				return;
			}
		}
		setLoggedIn(true);
		setUser(authPayload?.user || null);
		setPermissions(authPayload?.permissions || []);
	}

	function clearAuthState(socket, { redirect = true } = {}) {
		notifications.hide("auto-logout-warning");
		setCookies("");
		setLoggedIn(false);
		cookies.remove("authorization");
		if (socket) socket.disconnect();
		disconnectSocket();
		setUser(null);
		setPermissions([]);
		if (redirect) {
			history.push("/login");
		}
	}

	async function logout(socket, options = {}) {
		const {
			reason = "manual",
			skipServerLogout = false,
			skipBroadcast = false,
			redirectToLogin = true
		} = options;

		if (logoutInProgressRef.current) {
			clearAuthState(socket, { redirect: redirectToLogin });
			return;
		}

		logoutInProgressRef.current = true;
		const token = cookies.get(config.api.tokenAddress);

		if (token && !skipServerLogout) {
			try {
				await API.post(
					config.api.logout,
					{ reason },
					{
						headers: { "private-token": token },
						skipAuthFailureHandler: true
					}
				);
			} catch (_) {
				// Local logout still proceeds even if the server token is already invalid.
			}
		}

		if (!skipBroadcast) {
			try {
				localStorage.setItem(LOGOUT_SYNC_KEY, JSON.stringify({ at: Date.now(), reason }));
			} catch (_) {
				// Ignore storage broadcast failures.
			}
		}

		clearAuthState(socket, { redirect: redirectToLogin });
		logoutInProgressRef.current = false;
	}

	async function login(username, password, tenant) {
		if (!username) {
			return {
				success: false,
				error: "INVALID_CREDENTIALS",
				message: "Username is required."
			};
		}

		try {
			const res = await API.post(
				config.api.login,
				{
					username,
					tenant,
					password
				},
				{ timeout: 30000 }
			);
			if (res?.data?.token && res?.data?.user) {
				writeLastActivityAt(Date.now());
				applyAuthenticatedSession(res.data);
				if (!res.data.passwordChangeRequired) {
					history.push("/home");
				}
				return {
					success: true,
					...res.data
				};
			}

			return {
				success: false,
				error: "INVALID_CREDENTIALS",
				message: "Unable to sign in with those credentials."
			};
		} catch (error) {
			const authErrorCode = error?.response?.data?.error;
			if (authErrorCode === "ACCOUNT_LOCKED") {
				clearAuthState(undefined, { redirect: false });
			}
			const isTimeout =
				error?.code === "ECONNABORTED" ||
				String(error?.message || "").toLowerCase().includes("timeout");
			const isNetworkError =
				!error?.response &&
				(String(error?.code || "").toUpperCase() === "ERR_NETWORK" ||
					String(error?.message || "").toLowerCase().includes("network error"));
			const configuredApiUrl = config.api.API_URL || "https://api.apit.am";
			return {
				success: false,
				error:
					authErrorCode ||
					(isTimeout ? "NETWORK_ERROR" : isNetworkError ? "NETWORK_ERROR" : "AUTH_LOGIN_FAILED"),
				message: isTimeout
					? `Login timed out. Confirm the API is reachable at ${configuredApiUrl}.`
					: isNetworkError
						? `Cannot reach the API at ${configuredApiUrl}. Check the desktop API URL and ensure the backend is running.`
						: error?.response?.data?.message || error?.message || "Unable to complete sign-in."
			};
		}
	}

	async function checkToken() {
		const token = cookies.get(config.api.tokenAddress);
		if (!token) {
			clearAuthState(undefined, { redirect: false });
			return false;
		}

		// Retry transient failures (offline, timeout, API briefly unreachable/restarting)
		// instead of treating them as a logout. This matters most for the desktop app,
		// which runs this check on every cold start (auto-launch at boot, auto-update
		// restarts) — moments when networking/VPN may not be up yet even though the
		// stored session is still perfectly valid.
		const retryDelaysMs = [2000, 4000, 8000];

		for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
			try {
				const res = await API.post(config.api.verify, { token }, { timeout: 30000 });
				if (res.status === 200 && res?.data?.user) {
					applyAuthenticatedSession(
						res.data,
						cookies.get(config.api.tokenAddress) || token
					);
					const currentPath = history.location?.pathname || window.location.pathname;
					if (
						!res.data.passwordChangeRequired &&
						(currentPath === "/" ||
							currentPath === "/login" ||
							currentPath.startsWith("/reset-password"))
					) {
						history.push("/home");
					}
					return true;
				}
				clearAuthState(undefined, { redirect: false });
				return false;
			} catch (error) {
				if (isAuthenticationFailure(error)) {
					console.error(error);
					console.error(`Please don't edit cookies manually`);
					clearAuthState(undefined, { redirect: false });
					return false;
				}

				if (attempt < retryDelaysMs.length) {
					console.error("checkToken: transient failure, retrying", error);
					await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt]));
					continue;
				}

				// Never confirmed as an auth failure — leave the token in place so the
				// next successful check (retry, refocus, relaunch) signs the user back
				// in automatically instead of demanding their password again.
				console.error("checkToken: giving up after retries, keeping session token", error);
				return false;
			}
		}

		return false;
	}

	useEffect(() => {
		// const enabledModules = process.env.REACT_APP_APIT_ENABLED_MODULES;
		// console.log(`🚀 App started with modules: ${enabledModules || "all (default)"}`);

		// You can also show this in the UI
		// if (enabledModules) {
		// 	console.log(`📦 Loading modules: ${enabledModules}`);
		// }
		(async () => {
			if (lang === "" || lang === null) {
				setLang("en");
			}
			if (colorScheme === "" || colorScheme === null) {
				setColorScheme("dark");
			}

			if (activePlatform?.length > 0) {
				setActivePlatform(parseFloat(activePlatform));
			} else {
				setActivePlatform(0);
			}

			if (activePage?.length > 0) {
				setActivePage(parseFloat(activePage));
			} else {
				setActivePage(0);
			}

			if (subActivePage?.length > 0) {
				setSubActivePage(subActivePage);
			} else {
				setSubActivePage(0);
			}

			try {
				if (!isPublicPage) {
					await checkToken();
				}
			} finally {
				setAuthChecking(false);
			}
		})();
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		registerSessionExpiredHandler((error) => {
			const errorCode = error?.response?.data?.error;
			const revokeReason = error?.response?.data?.revokeReason;
			if (errorCode === "ACCOUNT_LOCKED") {
				notifications.show({
					color: "red",
					title: "Account locked",
					message: "This account has been locked and the current session has been signed out."
				});
			} else if (errorCode === "SESSION_REVOKED" || errorCode === "SESSION_INVALIDATED") {
				const messageByReason = {
					role_changed: "Permissions changed and all active sessions were invalidated.",
					role_permissions_changed:
						"Role permissions changed and the current session was invalidated.",
					admin_password_reset:
						"An administrator reset the password and the current session was signed out.",
					account_locked: "The account was locked and the current session was signed out.",
					inactive: "The session was signed out after 30 days without platform activity.",
					force_password_change_enabled:
						"A forced password change was enabled and the current session was signed out."
				};
				notifications.show({
					color: "orange",
					title: "Session ended",
					message:
						messageByReason[revokeReason] ||
						"The session is no longer valid and has been signed out."
				});
			} else if (errorCode === "IP_BLOCKED") {
				notifications.show({
					color: "red",
					title: "Access blocked",
					message: "This network address is blocked from using the platform."
				});
			}
			logout(undefined, { skipServerLogout: true });
		});

		return () => {
			registerSessionExpiredHandler(null);
		};
		// eslint-disable-next-line
	}, [loggedIn]);

	useEffect(() => {
		if (!loggedIn) {
			return undefined;
		}

		let isCheckingSession = false;

		// Skip verify calls for public routes (they don't need token verification)
		const isPublicRoute = () => {
			const currentPath = history.location?.pathname || window.location.pathname;
			return isPublicFrontendRoute(currentPath);
		};

		const verifyCurrentSession = async () => {
			// Don't verify token on public routes
			if (isPublicRoute()) {
				return;
			}

			const token = cookies.get(config.api.tokenAddress);

			if (!token) {
				logout(undefined, { skipServerLogout: true });
				return;
			}

			if (isCheckingSession) {
				return;
			}

			isCheckingSession = true;

			try {
				await API.post(config.api.verify, { token });
			} catch (error) {
				// Global API interceptor handles auth expiry and redirects to login.
			} finally {
				isCheckingSession = false;
			}
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				verifyCurrentSession();
			}
		};

		const sessionWatcher = window.setInterval(() => {
			if (!cookies.get(config.api.tokenAddress)) {
				logout(undefined, { skipServerLogout: true });
			}
		}, 15000);

		// Proactively refresh the private token on a timer so a long-lived session
		// never hits the hard JWT/cookie expiry while idle. On the web the response
		// interceptor refreshes on every request, so ordinary browsing keeps the
		// token fresh — but the desktop app can sit minimized in the tray for days
		// making no HTTP requests, so nothing triggers the interceptor and the
		// cookie silently reaches its exp, at which point Chromium deletes it and
		// the sessionWatcher above logs the user out. A timer keeps firing even in a
		// backgrounded/hidden renderer, so it closes that gap. refreshPrivateTokenIfNeeded
		// is a no-op unless the token is inside its refresh window, so this is cheap.
		const proactiveRefresh = () => {
			if (isPublicRoute()) return;
			if (!cookies.get(config.api.tokenAddress)) return;
			refreshPrivateTokenIfNeeded({}).catch(() => {});
		};
		proactiveRefresh();
		const tokenRefreshTimer = window.setInterval(proactiveRefresh, 6 * 60 * 60 * 1000);

		window.addEventListener("focus", verifyCurrentSession);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			window.clearInterval(sessionWatcher);
			window.clearInterval(tokenRefreshTimer);
			window.removeEventListener("focus", verifyCurrentSession);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedIn]);

	useEffect(() => {
		const handleForcedLogout = async (event) => {
			const reason = event?.detail?.reason;
			const reasonMessage = {
				role_changed: "Permissions changed and all active sessions were revoked.",
				role_permissions_changed: "Role permissions changed and this session was revoked.",
				admin_password_reset: "An administrator reset the password and this session was revoked.",
				account_locked: "The account was locked and this session was revoked."
			};

			notifications.show({
				color: "orange",
				title: "Session ended",
				message: reasonMessage[reason] || "This session was revoked by a security policy change."
			});

			await logout(undefined, {
				skipServerLogout: true,
				reason: reason || "session_revoked"
			});
		};

		window.addEventListener("apit:force-logout", handleForcedLogout);
		return () => {
			window.removeEventListener("apit:force-logout", handleForcedLogout);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!loggedIn) {
			return undefined;
		}
		const inactivityLogoutMs = Math.max(
			10_000,
			Number(config.session.inactivityLogoutMs) || config.session.oneMonthMs
		);
		const inactivityWarningMs = Math.min(
			Math.max(5_000, Number(config.session.inactivityWarningMs) || 60 * 1000),
			Math.max(inactivityLogoutMs - 1_000, 5_000)
		);
		let cancelWarningTimer = null;
		let cancelLogoutTimer = null;
		let lastActivityWrite = 0;

		const clearTimers = () => {
			if (cancelWarningTimer) {
				cancelWarningTimer();
				cancelWarningTimer = null;
			}
			if (cancelLogoutTimer) {
				cancelLogoutTimer();
				cancelLogoutTimer = null;
			}
		};

		const scheduleTimeouts = (activityAt = readLastActivityAt()) => {
			clearTimers();
			notifications.hide("auto-logout-warning");
			const idleStartsAt = activityAt + ACTIVITY_IDLE_AFTER_MS;
			const logoutAt = idleStartsAt + inactivityLogoutMs;
			const warningAt = logoutAt - inactivityWarningMs;

			cancelWarningTimer = setLongTimeout(() => {
				const roundedMinutes = Math.max(1, Math.ceil(inactivityWarningMs / 60000));
				notifications.show({
					id: "auto-logout-warning",
					color: "orange",
					title: "Session timeout warning",
					message: `You will be logged out in about ${roundedMinutes} minute${roundedMinutes === 1 ? "" : "s"} due to inactivity.`,
					autoClose: inactivityWarningMs
				});
			}, Math.max(warningAt - Date.now(), 0));

			cancelLogoutTimer = setLongTimeout(async () => {
				notifications.hide("auto-logout-warning");
				notifications.show({
					color: "orange",
					title: "Logged out for inactivity",
					message: "The session was invalidated because the platform was inactive for too long."
				});
				await logout(undefined, { reason: "inactive" });
			}, Math.max(logoutAt - Date.now(), 0));
		};

		const noteActivity = (broadcast = true) => {
			let activityAt = readLastActivityAt();
			if (broadcast) {
				const now = Date.now();
				activityAt = now;
				if (now - lastActivityWrite > 1000) {
					lastActivityWrite = now;
					writeLastActivityAt(now);
				}
			}
			scheduleTimeouts(activityAt);
		};

		const handleStorage = (event) => {
			if (event.key === ACTIVITY_SYNC_KEY && event.newValue) {
				scheduleTimeouts(Number(event.newValue) || readLastActivityAt());
				return;
			}

			if (event.key === LOGOUT_SYNC_KEY && event.newValue) {
				logout(undefined, {
					skipServerLogout: true,
					skipBroadcast: true
				});
			}
		};

		const handleActivity = () => noteActivity(true);
		scheduleTimeouts(readLastActivityAt());

		window.addEventListener("mousemove", handleActivity);
		window.addEventListener("mousedown", handleActivity);
		window.addEventListener("keydown", handleActivity);
		window.addEventListener("scroll", handleActivity, true);
		window.addEventListener("touchstart", handleActivity, { passive: true });
		window.addEventListener("storage", handleStorage);

		return () => {
			clearTimers();
			notifications.hide("auto-logout-warning");
			window.removeEventListener("mousemove", handleActivity);
			window.removeEventListener("mousedown", handleActivity);
			window.removeEventListener("keydown", handleActivity);
			window.removeEventListener("scroll", handleActivity, true);
			window.removeEventListener("touchstart", handleActivity);
			window.removeEventListener("storage", handleStorage);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedIn]);

	const toggleColorScheme = (value) => {
		setColorScheme((current) => value || (current === "dark" ? "light" : "dark"));
	};

	if (authChecking) {
		return (
			<Language.Provider value={{ lang, setLang, colorScheme, setColorScheme }}>
				<ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
					<MantineProvider theme={{ ...theme, colorScheme }} withGlobalStyles withNormalizeCSS>
						<Loading fullscreen />
					</MantineProvider>
				</ColorSchemeProvider>
			</Language.Provider>
		);
	}

	if (isPublicPage) {
		return (
			<Language.Provider value={{ lang, setLang, colorScheme, setColorScheme }}>
				<ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
					<MantineProvider theme={{ ...theme, colorScheme }} withGlobalStyles withNormalizeCSS>
						<Notifications position="top-right" zIndex={1000} />
						<Router history={history}>
							<Route path="/public/knowledge/article/:idOrSlug" component={PublicKnowledgeArticle} />
							<Route path={["/careers", "/public/meet/:token"]} component={PublicCareers} />
							<Route path="/f/:slug" component={PublicFormPage} />
							<Route path="/public/album/:token" component={PublicAlbum} />
						</Router>
					</MantineProvider>
				</ColorSchemeProvider>
			</Language.Provider>
		);
	}

	if (loggedIn && user) {
		return (
			<PermissionContext.Provider value={{ permissions, setPermissions }}>
				<Language.Provider value={{ lang, setLang, colorScheme, setColorScheme }}>
					<isLoggedIn.Provider value={{ login, logout, loggedIn }}>
						<UserContext.Provider value={{ user, setUser }}>
							<ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
								<MantineProvider
									theme={{ ...theme, colorScheme }}
									withGlobalStyles
									withNormalizeCSS
								>
									<DatesProvider settings={{ locale: getDateLocale(lang), firstDayOfWeek: 1 }}>
										<Notifications position="top-right" zIndex={1000} />
										{user?.forcePasswordChange || user?.passwordSetupRequired ? (
										<PasswordChangeGate
											user={user}
											onLogout={() => logout()}
											onPasswordChanged={(response) => {
												if (response?.token) {
													setCookies(response.token);
												}
												setUser(response?.user || null);
												setPermissions(response?.permissions || []);
												history.push("/home");
											}}
										/>
									) : (
										<SocketProvider>
											<GlobalCallingProvider lang={lang}>
												<ActivePlatform.Provider
													value={{
														activePlatform,
														setActivePlatform,
														activePage,
														setActivePage,
														subActivePage,
														setSubActivePage
													}}
												>
													<BrowserStateProvider>
														<IsMobileProvider>
															<Router history={history}>
																<Global.Provider
																	value={{
																		platforms,
																		setPlatforms,
																		filteredMenu,
																		setFilteredMenu,
																		currentPlatformIndex,
																		setCurrentPlatformIndex
																	}}
																>
																	<Main component={RouterWrapper} />
																</Global.Provider>
															</Router>
														</IsMobileProvider>
													</BrowserStateProvider>
												</ActivePlatform.Provider>
											</GlobalCallingProvider>
										</SocketProvider>
										)}
									</DatesProvider>
								</MantineProvider>
							</ColorSchemeProvider>
						</UserContext.Provider>
					</isLoggedIn.Provider>
				</Language.Provider>
			</PermissionContext.Provider>
		);
	} else {
		return (
			<Language.Provider value={{ lang, setLang, colorScheme, setColorScheme }}>
				<isLoggedIn.Provider value={{ login, logout, loggedIn }}>
					<ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
						<MantineProvider theme={{ ...theme, colorScheme }} withGlobalStyles withNormalizeCSS>
							<Notifications position="top-right" zIndex={1000} />
							<Router history={history}>
								<Route component={Login} />
							</Router>
						</MantineProvider>
					</ColorSchemeProvider>
				</isLoggedIn.Provider>
			</Language.Provider>
		);
	}
}

export default App;
